import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@zillow/constellation-10';
import { fixedScrollbarStyles } from './styles';

type FixedScrollbarProps = {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
  isTableBottomVisible: boolean;
};

export const FixedScrollbar = ({ scrollContainerRef, isVisible, isTableBottomVisible }: FixedScrollbarProps) => {
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [scrollbarStyle, setScrollbarStyle] = useState({ left: 0, width: 0 });
  const lastScrollLeft = useRef(0);

  const updateScrollbarVisibility = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const scrollbar = scrollbarRef.current;
    const content = contentRef.current;

    if (!scrollContainer || !scrollbar || !content) return;

    const canScroll = scrollContainer.scrollWidth > scrollContainer.clientWidth;
    const shouldShowFixedScrollbar = canScroll && isVisible && !isTableBottomVisible;

    if (shouldShowFixedScrollbar) {
      const tableRect = scrollContainer.getBoundingClientRect();
      setScrollbarStyle({ left: tableRect.left, width: tableRect.width });
      scrollbar.style.display = 'block';

      // Set width and scroll position to match container
      content.style.width = `${scrollContainer.scrollWidth}px`;
      scrollbar.scrollLeft = scrollContainer.scrollLeft;
    } else {
      scrollbar.style.display = 'none';
    }
  }, [scrollContainerRef, isVisible, isTableBottomVisible]);

  // Sync scrollbar scroll to table
  const handleScrollbarScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const scrollbar = scrollbarRef.current;
    if (!scrollContainer || !scrollbar) return;

    const newScrollLeft = scrollbar.scrollLeft;
    if (newScrollLeft !== lastScrollLeft.current) {
      lastScrollLeft.current = newScrollLeft;
      scrollContainer.scrollLeft = newScrollLeft;
    }
  }, [scrollContainerRef]);

  // Sync table scroll to scrollbar
  const handleContainerScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const scrollbar = scrollbarRef.current;
    const content = contentRef.current;
    if (!scrollContainer || !scrollbar || !content) return;

    const newScrollLeft = scrollContainer.scrollLeft;
    if (newScrollLeft !== lastScrollLeft.current) {
      lastScrollLeft.current = newScrollLeft;

      // Only update scrollbar position if it's currently visible
      if (isVisible && scrollbar.style.display !== 'none') {
        content.style.width = `${scrollContainer.scrollWidth}px`;
        scrollbar.scrollLeft = newScrollLeft;
      }
    }
  }, [scrollContainerRef, isVisible]);

  const handleWindowEvents = useCallback(() => {
    updateScrollbarVisibility();
  }, [updateScrollbarVisibility]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    updateScrollbarVisibility();

    const scrollbar = scrollbarRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (scrollbar) {
      scrollbar.addEventListener('scroll', handleScrollbarScroll);
    }
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleContainerScroll);
    }

    window.addEventListener('resize', handleWindowEvents);
    window.addEventListener('scroll', handleWindowEvents);

    return () => {
      if (scrollbar) {
        scrollbar.removeEventListener('scroll', handleScrollbarScroll);
      }
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleContainerScroll);
      }
      window.removeEventListener('resize', handleWindowEvents);
      window.removeEventListener('scroll', handleWindowEvents);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isVisible, updateScrollbarVisibility, handleScrollbarScroll, handleContainerScroll, handleWindowEvents]);

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      ref={scrollbarRef}
      css={fixedScrollbarStyles}
      style={{ left: `${scrollbarStyle.left}px`, width: `${scrollbarStyle.width}px` }}
    >
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values */}
      <Box ref={contentRef} css={{ height: '1px', minWidth: '100%' }} />
    </Box>
  );
};
